/**
 * Client access to signed-off show archives (sign-off backups + transcripts).
 * Admin sees all; a producer sees only their own. Served from the HDD store.
 */
import type { Project } from '@goanna-im/shared';

export interface ArchiveSummary {
  id: string;
  title: string;
  ownerId?: string;
  ownerName?: string;
  roomCode: string;
  startedAt: number;
  endedAt: number;
  recording: boolean;
  versionCount: number;
  transcriptLines: number;
}

export interface ArchiveVersion {
  at: number;
  version: number;
  title: string;
  project: Project;
  by?: string;
}
export interface ArchiveEvent {
  at: number;
  kind: string;
  data?: Record<string, unknown>;
}
export interface ArchiveTranscriptLine {
  at: number;
  text: string;
}

export interface SessionArchive {
  id: string;
  roomCode: string;
  title: string;
  ownerId?: string;
  ownerName?: string;
  startedAt: number;
  endedAt: number;
  recording: boolean;
  finalProject: Project;
  versions: ArchiveVersion[];
  events: ArchiveEvent[];
  transcript: ArchiveTranscriptLine[];
}

export async function listArchives(): Promise<ArchiveSummary[]> {
  const res = await fetch('/api/archives', { credentials: 'include' });
  if (!res.ok) return [];
  return ((await res.json()) as { archives?: ArchiveSummary[] }).archives ?? [];
}

export async function getArchive(id: string): Promise<SessionArchive | null> {
  const res = await fetch(`/api/archives/${encodeURIComponent(id)}`, { credentials: 'include' });
  if (!res.ok) return null;
  return ((await res.json()) as { archive?: SessionArchive }).archive ?? null;
}

/** Trigger a client-side file download from in-memory content. */
export function downloadBlob(name: string, content: string, mime: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

/** Render a transcript archive as a readable plain-text file. */
export function transcriptToText(a: SessionArchive): string {
  const when = new Date(a.startedAt).toLocaleString('en-AU');
  const lines = [
    `${a.title}`,
    `Recorded ${when} · room ${a.roomCode}`,
    `Producer: ${a.ownerName ?? 'Unknown'}`,
    '',
  ];
  if (a.transcript.length === 0) {
    lines.push('(No transcript was captured for this show.)');
  } else {
    const start = a.startedAt;
    for (const l of a.transcript) {
      const s = Math.max(0, Math.floor((l.at - start) / 1000));
      const mm = String(Math.floor(s / 60)).padStart(2, '0');
      const ss = String(s % 60).padStart(2, '0');
      lines.push(`[${mm}:${ss}] ${l.text}`);
    }
  }
  return lines.join('\n');
}
